import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { auth } from "@/lib/api";
import { Loader2, ShieldCheck, Trash2, History } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem("pr_token")}` });
const fmtDt = (v) => (v ? new Date(v).toLocaleString("en-IN") : "—");

function ExemptRow({ r, isAdmin, onRemove }) {
  return (
    <tr className={`border-t border-slate-100 ${r.active ? "" : "opacity-50"}`} data-testid={`jw-row-${r.material_code}`}>
      <td className="px-3 py-2 font-mono text-xs font-semibold text-slate-800">{r.material_code}</td>
      <td className="px-3 py-2 text-xs text-slate-600">{r.item_name || "—"}</td>
      <td className="px-3 py-2"><span className="px-2 py-0.5 text-[11px] rounded-full bg-amber-50 text-amber-800 border border-amber-200">{r.category}</span></td>
      <td className="px-3 py-2 text-xs text-slate-700 max-w-xs">{r.reason}</td>
      <td className="px-3 py-2 text-xs text-slate-500 whitespace-nowrap">{r.created_by_name || r.created_by}<div>{fmtDt(r.created_at)}</div></td>
      <td className="px-3 py-2 text-xs whitespace-nowrap" data-testid={`jw-status-${r.material_code}`}>
        {r.active ? <span className="text-emerald-700 font-semibold">Exempt</span>
          : <span className="text-slate-500">Removed by {r.removed_by_name || r.removed_by}<div>{fmtDt(r.removed_at)}</div></span>}
      </td>
      <td className="px-3 py-2 text-right">
        {isAdmin && r.active && (
          <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => onRemove(r)} data-testid={`jw-remove-${r.material_code}`}>
            <Trash2 size={14} />
          </Button>
        )}
      </td>
    </tr>
  );
}

export default function JobWorkExemptPanel({ renderPicker }) {
  const isAdmin = auth.user()?.role === "Admin";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRemoved, setShowRemoved] = useState(false);
  const [code, setCode] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [pickerKey, setPickerKey] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/list-prices/bom-exempt?include_removed=${showRemoved}`, { headers: headers() });
      setRows(r.data.rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load exemptions");
    } finally {
      setLoading(false);
    }
  }, [showRemoved]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/list-prices/bom-exempt`, { material_code: code, reason, category: "Job Work" }, { headers: headers() });
      toast.success(`${code} exempted from the BOM rule`);
      setCode(""); setReason(""); setPickerKey((k) => k + 1);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add exemption");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Remove BOM exemption for ${r.material_code}? New list prices and PRs for it will be blocked again.`)) return;
    try {
      await axios.delete(`${API}/list-prices/bom-exempt/${encodeURIComponent(r.material_code)}`, { headers: headers() });
      toast.success("Exemption removed");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Remove failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="jw-panel">
      <div className="bg-white border border-slate-200 rounded-sm p-4 space-y-3">
        <div className="flex items-start gap-2">
          <ShieldCheck size={18} className="text-amber-600 mt-0.5" />
          <div>
            <div className="text-sm font-semibold text-slate-800">Job Work items — BOM exemption</div>
            <p className="text-xs text-slate-500 mt-0.5 max-w-3xl">
              Items listed here skip the "must be in a Released BOM" rule, so buyers can create list prices and BOP PRs for them.
              All other SAP checks (UoM, Purchasing status Active) still apply.
            </p>
          </div>
        </div>
        {isAdmin ? (
          <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-2 items-start" data-testid="jw-add-form">
            <div>
              {renderPicker((c) => setCode(c), pickerKey)}
              {code && <div className="text-[11px] text-slate-500 mt-1">Selected: <span className="font-mono font-semibold text-slate-800" data-testid="jw-selected-code">{code}</span></div>}
            </div>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300}
              placeholder="Reason, e.g. Job work item — not in any BOM" data-testid="jw-reason-input" />
            <Button onClick={add} disabled={!code || reason.trim().length < 5 || saving}
              className="bg-cyan-700 hover:bg-cyan-800" data-testid="jw-add-btn">
              {saving && <Loader2 size={14} className="animate-spin mr-1" />} Exempt item
            </Button>
          </div>
        ) : (
          <div className="text-xs text-slate-500" data-testid="jw-readonly-note">Only Admins can add or remove exemptions.</div>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100">
          <div className="text-xs font-semibold text-slate-600">{rows.filter((r) => r.active).length} exempted item(s)</div>
          <label className="text-xs text-slate-600 inline-flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={showRemoved} onChange={(e) => setShowRemoved(e.target.checked)} data-testid="jw-show-removed" />
            <History size={12} /> Show removed
          </label>
        </div>
        {loading ? <div className="p-6 flex justify-center"><Loader2 className="animate-spin text-slate-400" /></div> : (
          <table className="w-full text-sm" data-testid="jw-table">
            <thead className="bg-slate-50 text-[11px] uppercase text-slate-500 text-left">
              <tr><th className="px-3 py-2">Item</th><th className="px-3 py-2">Description</th><th className="px-3 py-2">Type</th>
                <th className="px-3 py-2">Reason</th><th className="px-3 py-2">Added by</th><th className="px-3 py-2">Status</th><th /></tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={7} className="px-3 py-6 text-center text-xs text-slate-500" data-testid="jw-empty">No exempted items yet.</td></tr>}
              {rows.map((r) => <ExemptRow key={r.material_code} r={r} isAdmin={isAdmin} onRemove={remove} />)}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
